import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '@/components/ui/dialog';
import { useUserLogoutMutation } from '@/features/auth/api/useUsersLogoutMutation';
import { useStore } from '@/stores';
import { env } from '@/config/env';

import Modal2FAv2 from './modal-2fa-v2/modal-2FA-v2';

const ACTIVATE_2FA_STORAGE_KEY = 'force-2fa-activation-open';

const Force2FAModal = () => {
  const [openActivate2FA, setOpenActivate2FAState] = useState(
    () => sessionStorage.getItem(ACTIVATE_2FA_STORAGE_KEY) === 'true',
  );

  const setOpenActivate2FA = (open: boolean) => {
    if (open) {
      sessionStorage.setItem(ACTIVATE_2FA_STORAGE_KEY, 'true');
    } else {
      sessionStorage.removeItem(ACTIVATE_2FA_STORAGE_KEY);
    }
    setOpenActivate2FAState(open);
  };

  const profile = useStore((state) => state.profile);
  const accessToken = useStore((state) => state.accessToken);

  const { mutate: logout } = useUserLogoutMutation({
    onSuccess: () => {
      localStorage.removeItem('dashboard-merchant-storage');
      sessionStorage.removeItem(ACTIVATE_2FA_STORAGE_KEY);
      window.location.replace('/auth/login');
    },
    onError: (err) => {
      toast.error('Logout is failed', { description: err.message });
    },
  });

  const handleLogout = () => {
    if (!accessToken) return;
    logout({ access_token: accessToken });
  };

  const shouldForce2FA =
    env.use2FAv2 === 'true' && !!profile && (!profile.is_login_otp_enabled || openActivate2FA);

  if (!shouldForce2FA) return null;

  return (
    <>
      <Dialog
        open={!openActivate2FA}
        onOpenChange={(open) => {
          if (!open) return;
        }}
      >
        <DialogContent
          className="max-w-sm rounded-lg"
          showCloseButton={false}
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <div className="flex flex-col gap-4 py-2 text-center">
            <DialogTitle className="text-brand-dark text-2xl font-bold">
              Secure your account with 2FA
            </DialogTitle>
            <DialogDescription className="text-base text-gray-600">
              Two-factor authentication is required to keep your account protected. Activate 2FA now
              to continue using your account.
            </DialogDescription>
          </div>
          <DialogFooter>
            <div className="flex w-full flex-col items-center justify-between gap-2 sm:grid sm:grid-cols-2">
              <Button
                className="w-full cursor-pointer"
                variant={'destructive'}
                onClick={handleLogout}
              >
                Log out
              </Button>
              <Button
                className="bg-brand-blue hover:bg-brand-blue/90 w-full cursor-pointer"
                onClick={() => setOpenActivate2FA(true)}
              >
                Activate 2FA
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Modal2FAv2
        openModal={openActivate2FA}
        isTotpSetupConfirmed={!!profile?.is_totp_setup_confirmed}
        onSuccess={() => setOpenActivate2FA(false)}
        onBack={() => setOpenActivate2FA(false)}
      />
    </>
  );
};

export default Force2FAModal;
